<script>
import appConfig from '@/app.config'
import { Helper } from '@/mixin/helper'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import FormErrorDescription from './FormErrorDescription'
import ErrorDescriptionCard from '@/components/ErrorDescriptionCard'
import * as errorDescriptionApi from '@/services/rest-api-sms/lib/error-description/error-description'
import Multiselect from 'vue-multiselect'

export default {
  name: 'ErrorDescription',
  page: {
    title: 'Error Description',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    FormErrorDescription,
    ErrorDescriptionCard,
    Multiselect,
  },
  mixins: [Helper],
  data() {
    return {
      items: [],
      filteredItems: [],
      selectedItems: [],
      currentPage: 1,
      perPage: 12,
      totalRows: 0,
      pageOptions: [12, 24, 48, 96],
      totalPages: 0,
      filters: {
        search: null,
        type: null,
      },
      typeOptions: [
        { id: 'info', name: 'Info' },
        { id: 'warn', name: 'Warn' },
        { id: 'error', name: 'Error' },
      ],
      showFormModal: false,
      showDeleteModal: false,
      showPreviewModal: false,
      previewItem: null,
      formData: null,
      isLoading: false,
      isFormLoading: false,
      isDeleting: false,
      searchDebounceTimer: null,
    }
  },
  computed: {
    title() {
      return 'Error Description'
    },
    allSelected() {
      return this.items.length > 0 && this.selectedItems.length === this.items.length
    },
    someSelected() {
      return this.selectedItems.length > 0 && !this.allSelected
    },
  },
  watch: {
    'filters.search'(newVal) {
      clearTimeout(this.searchDebounceTimer)
      if (newVal === '' || newVal === null) {
        this.applyFiltersWithReset() // ← ganti
      } else {
        this.searchDebounceTimer = setTimeout(() => {
          this.applyFiltersWithReset() // ← ganti
        }, 500)
      }
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.isLoading = true
      try {
        await this.applyFilters()
      } catch (error) {
        this.$toast.error('Failed to load error descriptions')
        this.items = []
      } finally {
        this.isLoading = false
      }
    },
    async applyFilters() {
      const params = {
        limit: this.perPage,
        page: this.currentPage,
      }
      if (this.filters.search) {
        params.search = this.filters.search
      }
      if (this.filters.type) {
        params.filters = [`type eq '${this.filters.type.id}'`]
      }
      const response = await errorDescriptionApi.getList(params)
      this.items = response?.data?.paginated_result || []
      this.filteredItems = response?.data?.paginated_result || []
      this.totalRows = response?.metadata?.total_count || 0
      this.totalPages = response?.metadata?.total_page || 0
      this.currentPage = response?.metadata?.page || 1
      this.selectedItems = []
    },

    applyFiltersWithReset() {
      this.currentPage = 1
      this.applyFilters()
    },
    changePage(e) {
      this.currentPage = e
      this.applyFilters()
    },
    resetFilters() {
      this.filters = {
        search: null,
        type: null,
      }
      this.currentPage = 1
      this.applyFilters()
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedItems = []
      } else {
        this.selectedItems = [...this.items]
      }
    },
    isSelected(item) {
      return this.selectedItems.some((p) => p.code === item.code)
    },
    toggleSelect(item) {
      const index = this.selectedItems.findIndex((p) => p.code === item.code)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
    },
    openCreateModal() {
      this.formData = null
      this.showFormModal = true
    },
    openEditModal(item) {
      this.formData = {
        code: item.code || '',
        header: item.header || '',
        sub_header: item.sub_header || '',
        body: item.body || '',
        footer: item.footer || '',
        type: item.type || null,
      }
      this.showFormModal = true
    },
    /**
     * Opens a preview modal showing the card in full (modal) mode
     * so admins can see exactly how users will see this error
     */
    openPreviewModal(item) {
      this.previewItem = item
      this.showPreviewModal = true
    },
    closePreviewModal() {
      this.showPreviewModal = false
      this.previewItem = null
    },
    async handleFormSubmit(formData) {
      this.isFormLoading = true
      try {
        const { _edit, ...payload } = formData
        if (formData.code && _edit) {
          await errorDescriptionApi.update(this.formData.code, payload)
        } else {
          await errorDescriptionApi.create(payload)
        }
        await this.applyFilters()
        this.showFormModal = false
        this.$mcToast.success('Error description saved successfully')
      } catch (error) {
        this.$mcToast.error('Failed to save error description')
      } finally {
        this.isFormLoading = false
      }
    },
    async deleteSelected() {
      if (this.selectedItems.length === 0) return
      this.showDeleteModal = true
    },
    async confirmDelete() {
      this.isDeleting = true
      try {
        const codes = this.selectedItems.map((item) => item.code)
        for (const code of codes) {
          await errorDescriptionApi.deleteByCode(code)
        }
        this.$mcToast.success(`Successfully deleted ${this.selectedItems.length} item(s)`)
        this.selectedItems = []
        this.showDeleteModal = false
        await this.applyFilters()
      } catch (error) {
        this.$mcToast.error('Failed to delete items')
      } finally {
        this.isDeleting = false
      }
    },
    cancelDelete() {
      this.showDeleteModal = false
    },
  },
}
</script>
<template>
  <Layout>
    <PageHeader :title="title" />
    <!-- Main Container -->
    <div class="modern-wrapper">
      <!-- Top Control Bar (Search, Filters, Add Button) -->
      <div class="card shadow-sm border-0 mb-4 rounded-xl">
        <div class="card-body p-3 p-md-4">
          <div
            class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
          >
            <!-- Filters -->
            <div class="d-flex flex-column flex-md-row gap-3 flex-grow-1">
              <div class="search-box position-relative" style="min-width: 300px">
                <i class="mdi mdi-magnify search-icon text-muted"></i>
                <b-form-input
                  v-model="filters.search"
                  placeholder="Search by code or header..."
                  @input="applyFilters"
                  class="form-control-rounded bg-light border-0 ps-5"
                />
              </div>
              <div class="filter-box" style="min-width: 200px">
                <multiselect
                  v-model="filters.type"
                  :options="typeOptions"
                  :multiple="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :hide-selected="false"
                  :allow-empty="true"
                  placeholder="All Types"
                  label="name"
                  track-by="id"
                  deselect-label=""
                  select-label=""
                  selected-label=""
                  @input="applyFiltersWithReset"
                  class="modern-multiselect"
                />
              </div>
            </div>
            <!-- Actions -->
            <div class="d-flex align-items-center gap-2">
              <b-button
                v-if="filters.search || filters.type"
                variant="link"
                class="text-muted text-decoration-none px-2"
                @click="resetFilters"
              >
                Reset
              </b-button>
              <b-button
                variant="primary"
                class="rounded-pill shadow-sm px-4"
                @click="openCreateModal"
              >
                <i class="mdi mdi-plus me-1"></i> Add Record
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <!-- Contextual Selection Toolbar (Appears when items are selected) -->
      <b-collapse :visible="selectedItems.length > 0" class="mb-4">
        <div
          class="selection-toolbar d-flex align-items-center justify-content-between p-3 rounded-xl shadow-sm"
        >
          <div class="d-flex align-items-center gap-3">
            <b-form-checkbox
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleSelectAll"
              class="ms-1"
            >
              <span class="fw-medium ms-3 d-inline-block"
                >{{ selectedItems.length }} item(s) selected</span
              >
            </b-form-checkbox>
          </div>
          <div>
            <b-button variant="danger" class="rounded-pill px-3 shadow-sm" @click="deleteSelected">
              <i class="mdi mdi-trash-can-outline me-1"></i> Delete Selected
            </b-button>
          </div>
        </div>
      </b-collapse>
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner class="text-primary" style="width: 3rem; height: 3rem"></b-spinner>
        <h5 class="mt-3 text-muted">Loading Data...</h5>
      </div>
      <!-- Empty State -->
      <div
        v-else-if="items.length === 0"
        class="text-center py-5 bg-white rounded-xl shadow-sm border-0"
      >
        <div
          class="empty-state-icon mx-auto mb-3 bg-light text-muted d-flex align-items-center justify-content-center rounded-circle"
          style="width: 80px; height: 80px"
        >
          <i class="mdi mdi-text-box-search-outline" style="font-size: 2.5rem"></i>
        </div>
        <h4 class="fw-bold text-dark">No records found</h4>
        <p class="text-muted">
          Try adjusting your search or filters to find what you're looking for.
        </p>
        <b-button
          v-if="filters.search || filters.type"
          variant="outline-primary"
          class="rounded-pill mt-2"
          @click="resetFilters"
        >
          Clear All Filters
        </b-button>
      </div>
      <!-- Grid Data Cards — Now using ErrorDescriptionCard component -->
      <b-row v-else class="match-height">
        <b-col cols="12" md="6" xl="4" class="mb-4" v-for="item in items" :key="item.code">
          <ErrorDescriptionCard
            :code="item.code"
            :header="item.header"
            :sub-header="item.sub_header"
            :body="item.body"
            :footer="item.footer"
            :type="item.type"
            :compact="true"
            :selectable="true"
            :selected="isSelected(item)"
            :created-at="item.created_at"
            :created-by="item.created_by_name"
            :updated-at="item.updated_at"
            :updated-by="item.updated_by_name"
            @select="toggleSelect(item)"
            @edit="openEditModal(item)"
          />
        </b-col>
      </b-row>
      <!-- Pagination -->
      <div v-if="items.length > 0" class="card shadow-sm border-0 rounded-xl mt-2 mb-4">
        <div
          class="card-body p-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"
        >
          <div class="text-muted pagination-info d-flex align-items-center flex-wrap gap-2">
            <span>Showing</span>
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              size="sm"
              class="w-auto border-0 bg-light rounded-pill px-3"
              @change="resetFilters"
            />
            <span class="d-flex align-items-center flex-wrap gap-1">
              entries.
              <strong class="text-dark">{{
                filteredItems?.length > 0 ? (currentPage - 1) * perPage + 1 : 0
              }}</strong>
              -
              <strong class="text-dark">{{
                currentPage != totalPages
                  ? currentPage * perPage
                  : (currentPage - 1) * perPage + filteredItems?.length
              }}</strong>
              of <strong class="text-dark">{{ totalRows }}</strong>
            </span>
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="changePage"
            size="md"
            class="mb-0"
            aria-label="Page navigation"
          />
        </div>
      </div>
    </div>
    <!-- Form Modal (Create/Edit) -->
    <FormErrorDescription
      :modal.sync="showFormModal"
      :isLoading.sync="isFormLoading"
      :initialData="formData"
      @submit="handleFormSubmit"
    />

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      centered
      @ok="confirmDelete"
      @cancel="cancelDelete"
      header-bg-variant="danger"
      header-text-variant="white"
    >
      <div class="text-center py-3">
        <i class="mdi mdi-alert-circle-outline text-danger mb-3" style="font-size: 4rem"></i>
        <h5>Are you sure?</h5>
        <p class="text-muted">
          You are about to permanently delete <strong>{{ selectedItems.length }}</strong> error
          description(s). This action cannot be undone.
        </p>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="light" @click="cancel" :disabled="isDeleting" class="rounded-pill px-4">
          Cancel
        </b-button>
        <b-button variant="danger" @click="ok" :disabled="isDeleting" class="rounded-pill px-4">
          <b-spinner v-if="isDeleting" small class="me-1"></b-spinner>
          <i v-else class="mdi mdi-trash-can-outline me-1"></i>
          {{ isDeleting ? 'Deleting...' : 'Yes, Delete' }}
        </b-button>
      </template>
    </b-modal>
  </Layout>
</template>
<style scoped>
/* Gap Utility */
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}
/* Rounded Corners Modernization */
.rounded-xl {
  border-radius: 12px !important;
}
.form-control-rounded {
  border-radius: 50px !important;
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
}
/* Search Box positioning */
.search-box .search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  font-size: 1.2rem;
}
/* Contextual Toolbar */
.selection-toolbar {
  background-color: #e0f2fe;
  border: 1px solid #bae6fd;
}
/* Multiselect Styling Overrides to match theme */
.modern-multiselect >>> .multiselect__tags {
  border: 0;
  background-color: #f8f9fa;
  border-radius: 50px;
  padding: 0.6rem 2.5rem 0.6rem 1rem;
  min-height: 44px;
}
.modern-multiselect >>> .multiselect__placeholder {
  color: #6c757d;
  margin-bottom: 0;
  padding-top: 2px;
}
.modern-multiselect >>> .multiselect__select {
  top: 5px;
}

/* ========== PAGINATION STYLING ========== */
.pagination-info {
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
}

.modern-pagination >>> .page-item {
  margin: 0 2px;
}

.modern-pagination >>> .page-link {
  border-radius: 6px;
  border: 1.5px solid #cbd5e1;
  color: #4b5563;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 0.85rem;
  background-color: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 40px;
  text-align: center;
}

.modern-pagination >>> .page-link:hover:not(.disabled) {
  background-color: #f0f9ff;
  border-color: #bae6fd;
  color: #0d6efd;
}

/* ACTIVE PAGE - PRIMARY COLOR WITH WHITE TEXT */
.modern-pagination >>> .page-item.active .page-link {
  background-color: #0d6efd !important;
  border-color: #0d6efd !important;
  color: #ffffff !important;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.3);
}

/* DISABLED STATE */
.modern-pagination >>> .page-item.disabled .page-link {
  color: #adb5bd;
  background-color: #f8f9fa;
  border-color: #cbd5e1;
  cursor: not-allowed;
}
</style>
